import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const loadReCaptcha = (siteKey) => {
  if (!siteKey || typeof document === "undefined") return;
  if (document.getElementById("recaptcha-script")) return;

  const script = document.createElement("script");
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  script.id = "recaptcha-script";
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
};

export default function EventVote({
  fightId,
  fighterRedId,
  fighterBlueId,
  side = "red",
  userVote,
  onVotesLoaded,
  onRefreshVotes,
}) {
  const [loading, setLoading] = useState(false);
  const [localPick, setLocalPick] = useState(null);

  const siteKey =
    process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
    (typeof import.meta !== "undefined" ? import.meta.env?.VITE_RECAPTCHA_SITE_KEY : undefined);

  const storageKey = useMemo(() => {
    return fightId ? `warfc_pick_${fightId}` : null;
  }, [fightId]);

  useEffect(() => {
    if (siteKey) loadReCaptcha(siteKey);
  }, [siteKey]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") {
      setLocalPick(null);
      return;
    }

    try {
      const saved = window.localStorage.getItem(storageKey);
      setLocalPick(saved ? String(saved) : null);
    } catch {
      setLocalPick(null);
    }
  }, [storageKey]);

  const effectiveVote = useMemo(() => {
    if (userVote !== undefined && userVote !== null && userVote !== "") {
      return String(userVote);
    }
    return localPick ? String(localPick) : null;
  }, [userVote, localPick]);

  const currentFighterId = useMemo(() => {
    if (!fighterRedId || !fighterBlueId) return null;
    return String(side === "red" ? fighterRedId : fighterBlueId);
  }, [side, fighterRedId, fighterBlueId]);

  const isThisSide = Boolean(currentFighterId) && effectiveVote === currentFighterId;
  const hasVotedAny = Boolean(effectiveVote);

  const stopAll = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePick = useCallback(
    async (e) => {
      stopAll(e);

      if (!fightId || !currentFighterId) return;
      if (hasVotedAny || loading) return;
      if (!siteKey || !window.grecaptcha) {
        console.error("reCAPTCHA not ready");
        return;
      }

      try {
  setLoading(true);
  const captchaToken = await window.grecaptcha.execute(siteKey, { action: "vote" });

  const res = await axios.post(`https://warfc.com/api/events/fights/${fightId}/vote`, {
    fighterId: currentFighterId,
    captchaToken,
  });

  if (storageKey) {
    window.localStorage.setItem(storageKey, currentFighterId);
  }
  setLocalPick(currentFighterId);

  // THIS IS THE IMPORTANT PART: Pass the data back to the parent
  onVotesLoaded?.({
    user_vote: currentFighterId,
    red_votes: res.data?.red_votes,
    blue_votes: res.data?.blue_votes,
    total_votes: res.data?.total_votes,
    red_percent: res.data?.red_percent,
    blue_percent: res.data?.blue_percent,
  });

  // REMOVE OR COMMENT THIS OUT: 
  // if (onRefreshVotes) { await onRefreshVotes(); } 

} catch (err) {
  // ... same logic for 409 error ...
  // ALSO REMOVE onRefreshVotes here
} finally {
  setLoading(false);
}
    },
    [
      stopAll,
      fightId,
      currentFighterId,
      hasVotedAny,
      loading,
      siteKey,
      storageKey,
      onVotesLoaded,
      onRefreshVotes,
    ]
  );

  if (!fightId || !fighterRedId || !fighterBlueId || !currentFighterId) {
    return null;
  }

  return (
    <div
      className="flex flex-col items-center min-w-[60px] relative z-50"
      onClick={stopAll}
      onMouseDown={stopAll}
    >
      {!hasVotedAny || isThisSide ? (
        <button
          type="button"
          onClick={handlePick}
          onMouseDown={stopAll}
          onTouchStart={stopAll}
          disabled={hasVotedAny || loading}
          className={`group flex flex-col items-center transition-all duration-300 ${
            hasVotedAny ? "cursor-default" : "hover:scale-110 active:scale-90"
          }`}
        >
          <span
            className={`text-[10px] md:text-xs font-[1000] italic tracking-[0.2em] uppercase transition-colors ${
              isThisSide
                ? "text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]"
                : "text-white/60 group-hover:text-white"
            }`}
          >
            {loading ? "..." : isThisSide ? "YOUR PICK" : "PICK"}
          </span>

          {isThisSide && (
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1 shadow-[0_0_10px_#22d3ee] animate-pulse" />
          )}
        </button>
      ) : (
        <div className="h-4 w-4 bg-transparent" />
      )}
    </div>
  );
}